import * as React from 'react';
import { memo, useEffect, useState } from 'react';
import { LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { Modal, Upload } from 'antd';
import type { RcFile } from 'antd/es/upload/interface';
import ReactCrop, { centerCrop, Crop, makeAspectCrop } from 'react-image-crop';
import axios from 'axios';
import { getIconById } from '../utils';
import { IMAGE_TYPE } from '../admin-banners.constants';
import { ImageUploadComponentProps } from '../admin-banners.type';
import 'react-image-crop/dist/ReactCrop.css';
import {guidGenerator} from "mixins/helpers";

const ImageUploadComponent = memo(
    ({ selectImage, getInitialImageId, enableCrop }: ImageUploadComponentProps) => {
        const [loading, setLoading] = useState(false);
        const [croppedUrl, setCroppedUrl] = useState<string>();
        const [crop, setCrop] = useState<Crop>();
        const [image, setImage] = useState<HTMLImageElement>();

        useEffect(() => {
            const initialImageId = getInitialImageId();
            setCroppedUrl(initialImageId ? getIconById(initialImageId) : null);
        }, [getInitialImageId]);

        const beforeUpload = (file: RcFile) => {
            setCrop(null);
            setLoading(true);
            const reader = new FileReader();
            reader.addEventListener('load', () => {
                const img = new Image();
                img.src = reader.result?.toString() || '';
                img.onload = () => {
                    const { width, height } = img;
                    const size = Math.max(width, height);

                    const canvas = document.createElement('canvas');
                    canvas.width = size;
                    canvas.height = size;

                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(
                        img,
                        0,
                        0,
                        size,
                        size,
                        (size - width) / 2,
                        (size - height) / 2,
                        size,
                        size
                    );

                    const squareImage = new Image();
                    squareImage.src = canvas.toDataURL(IMAGE_TYPE);

                    squareImage.onload = () => {
                        setImage(squareImage);
                        setLoading(false);
                        setCrop(
                            centerCrop(
                                makeAspectCrop(
                                    {
                                        unit: '%',
                                        width: 100,
                                        height: 100,
                                    },
                                    1,
                                    size,
                                    size
                                ),
                                size,
                                size
                            )
                        );
                    };
                };
            });
            reader.readAsDataURL(file as unknown as Blob);
            return false;
        };

        const uploadImage = async (canvas: HTMLCanvasElement) => {
            return new Promise((resolve) =>
                canvas.toBlob(async (blob: Blob) => {
                    let file = new File([blob], IMAGE_TYPE.replace('/', '.'), { type: IMAGE_TYPE });
                    const formData = new FormData();
                    formData.append('file', file);
                    formData.append('parent', '/isna-portal-cms/external-resources/images');
                    formData.append('name', guidGenerator() + IMAGE_TYPE.split('/')[1]);

                    const res = await axios.post(
                        '/admin/rest-v2/cs/cms/ru/content/content/createMedia',
                        formData
                    );
                    resolve(res.data);
                }, IMAGE_TYPE)
            );
        };

        const onCropFinish = () => {
            if (image && crop.width && crop.height) {
                const canvas = document.createElement('canvas');
                canvas.width = (crop.width * image.width) / 100;
                canvas.height = (crop.height * image.height) / 100;
                const ctx = canvas.getContext('2d');

                ctx.drawImage(
                    image,
                    (crop.x * image.width) / 100,
                    (crop.y * image.height) / 100,
                    (crop.width * image.width) / 100,
                    (crop.height * image.height) / 100,
                    0,
                    0,
                    canvas.width,
                    canvas.height
                );

                setImage(null);
                setCroppedUrl(canvas.toDataURL());
                uploadImage(canvas).then(selectImage);
            }
        };

        const uploadButton = (
            <div>
                {loading ? <LoadingOutlined /> : <PlusOutlined />}
                <div style={{ marginTop: 8 }}>Загрузить</div>
            </div>
        );

        return (
            <>
                <Upload
                    name="image"
                    listType="picture-card"
                    showUploadList={false}
                    beforeUpload={beforeUpload}
                    accept="image/jpeg, image/png"
                >
                    {croppedUrl ? (
                        <img
                            src={croppedUrl}
                            alt="crop"
                            style={{ width: '100%', overflow: 'hidden' }}
                        />
                    ) : (
                        uploadButton
                    )}
                </Upload>
                {image && (
                    <Modal
                        onOk={onCropFinish}
                        onCancel={() => setImage(null)}
                        open={Boolean(image)}
                    >
                        <ReactCrop
                            crop={crop}
                            onChange={(_, newCrop) => setCrop(newCrop)}
                            aspect={1}
                            style={{ margin: '20px', pointerEvents: enableCrop ? 'auto' : 'none' }}
                        >
                            <img
                                src={image.src}
                                alt="cropped image"
                            />
                        </ReactCrop>
                    </Modal>
                )}
            </>
        );
    }
);

export default ImageUploadComponent;
